// src/components/WiggiWiggiGame/GameBoard.js
import React, { useMemo } from 'react';
import Unit from './Unit';
import { hexToPolygonPoints, generateHexGridGeometry } from './utils';
// Make sure necessary constants are imported
import {
    GRID_WIDTH, GRID_HEIGHT, HEX_SIZE, HEX_WIDTH, HEX_HEIGHT, SVG_WIDTH, SVG_HEIGHT,
    CENTER_HEX_ID, // Include UNIT_TYPES if needed by getHexFill indirectly? No, gameUnits has type.
    COLOR_BUSH, COLOR_IDOL_GOLD, COLOR_DEFAULT_HEX, COLOR_ATTACK, COLOR_SELECTED, COLOR_MOVE, COLOR_IDOL_CENTER,
    OWNER_PLAYER, OWNER_COMPUTER, // Import owner constants
    STATUS_PLAYING
} from './constants';

const GameBoard = ({
    gameUnits,
    selectedHex,
    possibleMoves,
    possibleAttacks,
    clearedBushes,
    bushHexes,
    visibilityData, // Contains allVisible set
    onHexClick,
    currentTurn,
    gameStatus // gameStatus is needed for visibility logic
}) => {

    // Generate hex grid geometry - memoize to avoid recalculation on every render
    const hexGrid = useMemo(() => generateHexGridGeometry(GRID_WIDTH, GRID_HEIGHT, HEX_WIDTH, HEX_HEIGHT), []);

    // Determine the fill color for a hex based on game state
    // This function likely needs access to gameUnits from props if not already passed indirectly
    const getHexFill = (hex) => {
        const hexId = hex.id;
        // Ensure gameUnits is accessible here, assuming it's passed via props or context if needed for this function directly
        const unitOnHex = gameUnits[hexId];

        // Priority order for highlighting/coloring:
        if (unitOnHex && unitOnHex.type === 'IDOL_STONE') {
             return (hexId === CENTER_HEX_ID) ? COLOR_IDOL_CENTER : COLOR_IDOL_GOLD;
        }
        // These rely on props passed to GameBoard: selectedHex, possibleAttacks, possibleMoves
        if (possibleAttacks.includes(hexId)) { return COLOR_ATTACK; }
        if (hexId === selectedHex) { return COLOR_SELECTED; }
        if (possibleMoves.includes(hexId)) { return COLOR_MOVE; }
        // These rely on props passed to GameBoard: bushHexes, clearedBushes
        if (bushHexes.has(hexId) && !clearedBushes[hexId]) {
             return COLOR_BUSH;
        }
        return COLOR_DEFAULT_HEX;
    };

    return (
        <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '0.25rem', width: '100%', maxWidth: `${SVG_WIDTH + 40}px` }}>
            <svg width="100%" viewBox={`-20 -20 ${SVG_WIDTH + 40} ${SVG_HEIGHT + 40}`} style={{ backgroundColor: '#f0f0f0', display: 'block' }}>
                {/* Hex Background Layer */}
                <g>
                    {hexGrid.map(hex => (
                        <polygon
                            key={hex.id}
                            points={hexToPolygonPoints(hex, HEX_SIZE)}
                            fill={getHexFill(hex)} // Pass necessary data if getHexFill needs more than props
                            stroke="#718096"
                            strokeWidth="1"
                            onClick={() => onHexClick(hex)} // Pass hex object
                            style={{ cursor: (currentTurn === OWNER_PLAYER && gameStatus === STATUS_PLAYING) ? 'pointer' : 'default' }}
                        />
                    ))}
                </g>
                {/* Unit Layer */}
                <g>
                    {/* Render units based on gameUnits state */}
                    {Object.entries(gameUnits).map(([hexId, unitData]) => {
                        // --- Visibility Calculation ---
                        // Standard visibility: hex is seen OR game is over OR it's an Idol
                        const standardVisibility = visibilityData.allVisible.has(hexId)
                                                 || gameStatus !== STATUS_PLAYING
                                                 || unitData.type === 'IDOL_STONE';

                        // Special case: Is it an activated ENEMY bomber?
                        const isAlwaysVisibleBomber =
                            unitData.owner === OWNER_COMPUTER &&
                            unitData.type === 'COCONUT_BOMBER' &&
                            unitData.isActivated;

                        // Final visibility is true if either condition is met
                        const finalIsVisible = standardVisibility || isAlwaysVisibleBomber;
                        // --- End Visibility Calculation ---

                        return (
                            <Unit
                                key={`unit-${unitData.id || hexId}`}
                                unitData={unitData}
                                hexId={hexId}
                                hexWidth={HEX_WIDTH}
                                hexHeight={HEX_HEIGHT}
                                hexSize={HEX_SIZE}
                                // Pass the final calculated visibility
                                isVisible={finalIsVisible}
                                isExploding={!!unitData.exploding}
                            />
                        );
                    })}
                </g>
            </svg>
        </div>
    );
};

export default GameBoard;